import React, { useGlobal } from "reactn"
import { graphql, StaticQuery } from "gatsby"
import Image, { ImageProps } from "./Image"
import Slideshow from "./Slideshow"
import convert from "../utils/convert"
import Dialog from "./Dialog"

export interface ApartmentProps {
  id: string
  title: string
  subtitle: string
  price: string
  sold: boolean
  bidUrl?: string
  text: string
  colors: string[]
  rom: {
    baderom: number
    soverom: number
    entre: number
  }
  areal: {
    inside: string
    primaryRoom: string
    outside: string
  }
  gallery?: ImageProps[]
  setActiveApartmentId: Function
  className?: string
  style?: object
  floorLegend?: string
}

export const Apartment: React.FC<ApartmentProps> = ({
  id,
  title,
  subtitle,
  price,
  sold,
  bidUrl,
  text,
  colors,
  rom,
  areal,
  setActiveApartmentId,
  className,
  style
}) => {
  let excerpt = text.replace(/<(?:.|\n)*?>/gm, "").substring(0, 125)
  if(text.length > 100) excerpt = `${excerpt}..`

  return (
    <div
      className={className}
      style={style}
    >
      <h2 className="block text-3xl mb-0">
        {title}
      </h2>

      <p className="text-accent block text-base mb-0">
        {subtitle}
      </p>

      <section className="mb-4 mt-2 flex justify-between items-center">
        <p className={`text-lg font-bold mb-0 ${sold ? 'line-through' : ''}`}>
          {price}
        </p>

        <a
          href={bidUrl}
          target="_blank"
          rel="noopener"
          className={`border-2 border-white border-solid px-3 px-1 rounded-sm hover:border-pink-500 hover:bg-pink-500 hover:text-white ${sold ? 'disabled' : ''}`}
        >
          {sold ? 'Solgt!' : 'Gi bud'}
        </a>
      </section>

      <section className="mt-4 pt-4 border-t border-gray-800 border-solid flex-col items-start">
        <p className="text-base mb-2">
          {excerpt}
        </p>

        <button
          className="text-accent text-sm"
          onClick={() => {
            setActiveApartmentId(id)
          }}
        >
          Se mer
        </button>
      </section>

      <section className="mt-4 pt-4 border-t border-gray-800 border-solid flex">
        <h3 className="mb-2 flex-1 text-base">
          Farger
        </h3>

        <div className="flex flex-wrap items-center h-8 flex-2 pl-2 mb-0">
          {colors.map((color: string, index: number) => (
            <div
              key={index}
              className="rounded w-4 h-4 mr-2"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </section>

      <section className="mt-4 pt-4 border-t border-gray-800 border-solid flex text-base">
        <h3 className="mb-2 flex-1 text-base">
          Antall rom
        </h3>

        <ul className="flex-2 pl-2 mb-0">
          <li className="mt-0 flex items-center">
            <Image
              src="https://assets.bonum.no/bilder/boliger/holgerslystveien-10/icons/bathtub.svg"
              alt="Antall baderom"
              className="w-6 mr-4"
              style={{
                filter: "invert(1)"
              }}
            />

            {rom.baderom} baderom
          </li>
          <li className="mt-0 flex items-center">
            <Image
              src="https://assets.bonum.no/bilder/boliger/holgerslystveien-10/icons/bed.svg"
              alt="Antall soverom"
              className="w-6 mr-4"
              style={{
                filter: "invert(1)"
              }}
            />

            {rom.soverom} soverom
          </li>
          <li className="mt-0 flex items-center">
            <Image
              src="https://assets.bonum.no/bilder/boliger/holgerslystveien-10/icons/door.svg"
              alt="Antall entre"
              className="w-6 mr-4"
              style={{
                filter: "invert(1)"
              }}
            />

            {rom.entre} entré
          </li>
        </ul>
      </section>

      <section className="mt-4 pt-4 border-t border-gray-800 border-solid flex text-base">
        <h3 className="mb-2 flex-1 text-base">
          Areal
        </h3>

        <ul className="flex-2 pl-2 mb-0">
          <li className="mt-0 flex items-center">
            <Image
              src="https://assets.bonum.no/bilder/boliger/holgerslystveien-10/icons/measurment.svg"
              alt="Størrelse inne"
              className="w-6 mr-4"
              style={{
                filter: "invert(1)"
              }}
            />

            {areal.inside} inneareal
          </li>
          <li className="mt-0 flex items-center">
            <Image
              src="https://assets.bonum.no/bilder/boliger/holgerslystveien-10/icons/sofa.svg"
              alt="Størrelse primær rom"
              className="w-6 mr-4"
              style={{
                filter: "invert(1)"
              }}
            />

            {areal.primaryRoom} primær rom
          </li>
          <li className="mt-0 flex items-center">
            <Image
              src="https://assets.bonum.no/bilder/boliger/holgerslystveien-10/icons/sun.svg"
              alt="Størrelse ute"
              className="w-6 mr-4"
              style={{
                filter: "invert(1)"
              }}
            />

            {areal.outside} uteareal
          </li>
        </ul>
      </section>
    </div>
  )
}

interface ApartmentSlideProps {
  node: {
    id: string,
    frontmatter: ApartmentProps
  }
}

interface ApartmentQueryProps {
  data: {
    allMarkdownRemark: {
      edges: ApartmentSlideProps[]
    }
  }
}

const Apartments: React.FC<ApartmentQueryProps> = ({ data }) => {
  const { edges: apartments } = data.allMarkdownRemark
  const [ activeApartmentId, setActiveApartmentId ] = useGlobal<any>("activeApartmentId")

  const activeApartment = apartments[
    apartments.findIndex(({ node }) => node.id === activeApartmentId)
  ] || null

  return (
    <div>
      <Slideshow
        className="px-3"
        options={{
          roundLengths: true,
          slidesPerView: 5,
          breakpoints: {
            1440: {
              slidesPerView: 4
            },
            992: {
              slidesPerView: 3
            },
            768: {
              slidesPerView: 2
            },
            500: {
              slidesPerView: 1
            }
          }
        }}
      >
        {apartments.map(({ node: apartment }) => (
          <div key={apartment.id}>
            <Apartment
              id={apartment.id}
              title={apartment.frontmatter.title}
              subtitle={apartment.frontmatter.subtitle}
              sold={apartment.frontmatter.sold}
              price={apartment.frontmatter.price}
              bidUrl={apartment.frontmatter.bidUrl}
              text={apartment.frontmatter.text}
              colors={apartment.frontmatter.colors}
              rom={apartment.frontmatter.rom}
              areal={apartment.frontmatter.areal}
              setActiveApartmentId={setActiveApartmentId}
              className="px-3"
            />
          </div>
        ))}
      </Slideshow>

      {activeApartment && (
        <ApartmentDialog
          {...activeApartment.node.frontmatter}
          shown={!!activeApartment}
          setActiveApartmentId={setActiveApartmentId}
        />
      )}
    </div>
  )
}

interface ApartmentDialog {
  shown: boolean
  title: string
  text: string
  gallery?: ImageProps[]
  setActiveApartmentId: Function
}

const ApartmentDialog: React.FC<ApartmentDialog> = ({
  shown,
  title,
  text,
  gallery,
  setActiveApartmentId
}) => (
  <Dialog
    title={title}
    shown={shown}
    toBody={true}
    width={600}
    onClose={() => setActiveApartmentId(null)}
  >
    <div
      className="py-3 px-6"
      dangerouslySetInnerHTML={{
        __html: convert(text)
      }}
    />

    {gallery && (
      <section className="my-3 py-3 px-6 border-t border-gray-200 border-solid text-base">
        {gallery.map((image, index) => (
          <a
            key={index}
            href={image.src.publicURL}
            target="_blank"
            className="inline-block w-1/3 hover:opacity-50 lg:w-1/2"
          >
            <Image
              src={image.src}
              alt={image.alt}
            />
          </a>
        ))}
      </section>
    )}
  </Dialog>
)

const apartmentsQuery = graphql`
  query ApartmentsQuery {
    allMarkdownRemark(
      sort: { order: ASC, fields: [frontmatter___order] },
      filter: {
        frontmatter: {
          templateKey: { eq: "apartment" },
          shown: { eq: true }
        }
      }
    ) {
      edges {
        node {
          id
          frontmatter {
            templateKey
            shown
            order
            title
            subtitle
            sold
            price
            bidUrl
            text
            colors
            rom {
              baderom
              soverom
              entre
            }
            areal {
              inside
              primaryRoom
              outside
            }
            gallery {
              src {
                childImageSharp {
                  fluid(maxHeight: 500, quality: 100) {
                    ...GatsbyImageSharpFluid
                  }
                }
                publicURL
              }
              alt
            }
          }
        }
      }
    }
  }
`

export default () => (
  <StaticQuery
    query={apartmentsQuery}
    render={(data) => <Apartments data={data} />}
  />
)
